
import React from 'react';
import { StateTaxResult } from '../detail';

interface StateResultsPanelProps {
  taxResult: StateTaxResult;
  frequency: 'annual' | 'monthly' | 'biweekly';
  formatCurrency: (amount: number) => string;
}

/**
 * Returns the appropriate Remix Icon class for a given tax category.
 * Falls back to a generic subtract icon if the category is unknown.
 */
const getIconForCategory = (category: string): string => {
  const icons: Record<string, string> = {
    'Federal Tax': 'ri-government-line',
    'State Tax': 'ri-map-pin-line',
    'Social Security': 'ri-shield-check-line',
    'Medicare': 'ri-heart-pulse-line',
  };
  return icons[category] ?? 'ri-subtract-line';
};

const StateResultsPanel: React.FC<StateResultsPanelProps> = ({
  taxResult,
  frequency,
  formatCurrency,
}) => {
  // Guard against invalid `taxResult` props
  if (!taxResult) {
    return (
      <div className="bg-gray-800 text-white p-4 rounded">
        <p className="text-red-400">No tax data available.</p>
      </div>
    );
  }

  // Determine divisor based on selected frequency; protect against division by zero
  const divisor = frequency === 'monthly' ? 12 : frequency === 'biweekly' ? 26 : 1;
  const safeDivisor = divisor || 1;

  const displayNet = taxResult.netSalary / safeDivisor;
  const freqLabel =
    frequency === 'monthly'
      ? 'per month'
      : frequency === 'biweekly'
      ? 'per paycheck'
      : 'per year';

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-8 text-white sticky top-24">
      <div className="mb-8">
        <p className="text-sm text-gray-400 mb-2">Your Take-Home Pay</p>
        <p className="text-5xl font-bold mb-1">{formatCurrency(displayNet)}</p>
        <p className="text-sm text-gray-400">{freqLabel}</p>
      </div>

      <div className="space-y-4">
        {/* Gross Salary */}
        <div className="flex items-center justify-between py-3 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-green-400"></i>
            </div>
            <span className="text-sm text-gray-300">Gross Salary</span>
          </div>
          <span className="font-semibold">
            {formatCurrency(taxResult.grossSalary / safeDivisor)}
          </span>
        </div>

        {/* Tax Breakdown */}
        {taxResult.breakdown.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between py-3 border-b border-gray-700"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i
                  className={`${getIconForCategory(item.category)} text-xl text-red-400`}
                ></i>
              </div>
              <span className="text-sm text-gray-300">{item.category}</span>
            </div>
            <span className="font-semibold text-red-400">
              -{formatCurrency(item.amount / safeDivisor)}
            </span>
          </div>
        ))}

        {/* Net Pay Summary */}
        <div className="flex items-center justify-between py-4 border-t-2 border-gray-600 mt-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-wallet-3-line text-xl text-teal-400"></i>
            </div>
            <span className="font-semibold">Net Pay</span>
          </div>
          <span className="text-2xl font-bold text-teal-400">
            {formatCurrency(displayNet)}
          </span>
        </div>

        {/* Effective Tax Rate */}
        <div className="bg-gray-800 rounded-lg p-4 mt-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Effective Tax Rate</span>
            <span className="font-semibold text-yellow-400">
              {(taxResult.effectiveRate * 100).toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateResultsPanel;
